/**
 * Created by Daniel Wang on 2/6/2024.
 * Description: usage report component
 * ------ maintenance history ------
 */

import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '../../../redux';
import { businessConstants } from '../../../constants/business.constants';
import { ScreenActionTypes } from '../../../redux/actions/screen.actions';
import { AuditTrailService } from '../../../tamalelibs/services/audit-trail.service';
import { LoginUsageReport } from './usage-report.model';
import { DatePipe } from '@angular/common';
import { AdminPagesService } from '../../../tamalelibs/services/admin-page.service';
import { UtilsService } from '../../../tamalelibs/services/utils.service';

@Component({
    selector: 'tam-usage-report',
    templateUrl: './usage-report.component.html',
    styleUrls: ['./usage-report.component.scss']
})
export class LoginUsageReportComponent implements OnInit, OnDestroy {
    // #region public properties
    columnDefs: Array<ColDef>;
    gridOptions: GridOptions;
    noDataStyle = 'none';
    reloading = false;
    rowData = [];
    // #endregion

    // #region private properties
    private _destroySubscriptions: Array<Subscription> = [];
    private _gridApi: GridApi;
    private _gridColumnApi: ColumnApi;
    // #endregion

    // #region constructor
    constructor(
        private _adminPageService: AdminPagesService,
        private _auditTrailService: AuditTrailService,
        private _datepipe: DatePipe,
        private _route: ActivatedRoute,
        private _store: Store<AppState>,
        private _utilsService: UtilsService
    ) {
    }
    // #endregion

    // #region angular functions
    ngOnInit() {
        this._initPage();
        this._initAGGridProperties();
        this._initAGGridColumns();
        this._initAGGridData();
        this._initSubscribion();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }
    // #endregion

    // #region public functions
    onGridReady(params) {
        this._gridApi = params.api;
        this._gridColumnApi = params.columnApi;
        this._gridApi.sizeColumnsToFit();
    }
    // #endregion

    // #region private functions
    private _initAGGridData() {
        this.reloading = true;
        this._auditTrailService.getUsageReport().pipe(take(1)).subscribe(res => {
            if (res) {
                const data = [];
                res.forEach(item => {
                    data.push(LoginUsageReport.parse(item));
                });
                this.rowData = data;
                this.reloading = false;
            }
        });
    }
    private _initAGGridColumns() {
        const cellStyleHandler = () => {
            return {
                'white-space': 'nowrap',
                'overflow': 'hidden',
                'text-overflow': 'ellipsis',
                'display': 'block',
                'border': 'none'
            };
        };
        this.columnDefs = [{
            headerName: 'ID',
            field: 'loginID',
            colId: 'loginID',
            menuTabs: [],
            cellStyle: cellStyleHandler
        }, {
            headerName: 'User Name',
            field: 'username',
            colId: 'username',
            menuTabs: [],
            cellStyle: cellStyleHandler
        }, {
            headerName: 'Last Login',
            field: 'lastLogin',
            colId: 'lastLogin',
            menuTabs: [],
            cellStyle: cellStyleHandler,
            cellRenderer: (data) => {
                return this._datepipe.transform(data.value, 'MM/dd/yyyy HH:mm');
            }
        }, {
            headerName: 'Last Login Type',
            field: 'lastLoginType',
            colId: 'lastLoginType',
            menuTabs: [],
            cellStyle: cellStyleHandler
        }, {
            headerName: 'Last Active',
            field: 'lastActive',
            colId: 'lastActive',
            menuTabs: [],
            cellStyle: cellStyleHandler,
            cellRenderer: (data) => {
                return this._datepipe.transform(data.value, 'MM/dd/yyyy HH:mm');
            }
        }, {
            headerName: 'Last Active Type',
            field: 'lastActiveType',
            colId: 'lastActiveType',
            menuTabs: [],
            cellStyle: cellStyleHandler
        }, {
            headerName: 'Last Submission Date',
            field: 'lastSubmission',
            colId: 'lastSubmission',
            menuTabs: [],
            cellStyle: cellStyleHandler,
            cellRenderer: (data) => {
                return this._datepipe.transform(data.value, 'MM/dd/yyyy HH:mm');
            }
        }, {
            headerName: 'Content Count',
            field: 'contentCount',
            colId: 'contentCount',
            menuTabs: [],
            cellStyle: cellStyleHandler
        }];
    }

    private _initAGGridProperties() {
        this.gridOptions = {
            defaultColDef: {
                sortable: true,
                resizable: true,
                menuTabs: []
            },
            cacheBlockSize: 50,
            suppressCellFocus: true,
            enableBrowserTooltips: true,
            rowSelection: 'single',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns:true,
        };
    }

    private _initPage() {
        const currentPageInfo: any = {
            url: this._route.snapshot.url,
            name: 'Login Usage Report',
            screenType: businessConstants.screenType.auditLog,
            type: 'USAGE_REPORT'
        };
        this._store.dispatch({
            type: ScreenActionTypes.SET_VALUE,
            payload: currentPageInfo
        });
    }

    private _initSubscribion() {
        this._destroySubscriptions.push(
            this._adminPageService.exportToExcel$.pipe(
                debounceTime(1000),
            ).subscribe(res => {
                const currentDate = this._datepipe.transform(new Date(), 'yyyyMMddHHmm');
                const excelName = 'LoginUsageReport' + currentDate + '.xlsx';
                this._utilsService.exportToXLS(this._gridColumnApi, this.columnDefs, this._gridApi, 'Sheet1', excelName);
            })
        );
    }
    // #endregion
}
